import { useEffect, useState } from "react";
import { Search, BookOpen, GraduationCap, FileText, Wrench, ScrollText } from "lucide-react";
import { fetchResources, fetchFacets } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";

const TYPE_ICON = {
  publication: ScrollText,
  workshop: GraduationCap,
  guide: BookOpen,
  report: FileText,
  toolkit: Wrench,
};

const TYPE_LABEL = {
  publication: "Publication",
  workshop: "Workshop",
  guide: "Guide",
  report: "Report",
  toolkit: "Toolkit",
};

const TOPIC_LABEL = {
  "ecological-restoration": "Ecological Restoration",
  "native-seed": "Native Seed",
  "biodiversity": "Biodiversity",
  "indigenous-led": "Indigenous-led",
  "landscape-rehabilitation": "Landscape Rehabilitation",
  "restoration-economy": "Restoration Economy",
  "workforce": "Workforce",
  "training": "Training",
};

export default function ResourcesPage() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ types: [], topics: [] });
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [topic, setTopic] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchFacets().then(setFacets).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchResources({ q: q || undefined, type, topic })
        .then(setItems)
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, type, topic]);

  return (
    <div data-testid="page-resources" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <SectionHeader
        overline="Knowledge & Training"
        title={<>Practical guidance for <span className="italic text-ra-ochre">restoration practitioners</span>.</>}
        lede="A growing library of publications, field workshops, technical guides and toolkits — produced with Australia's leading restoration ecologists and Traditional Owners."
        testId="resources-header"
      />

      {/* Filters */}
      <div className="mt-14 border border-ra-line bg-ra-bone">
        <div className="p-6 lg:p-8 grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-6 relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ra-soft" />
            <input
              data-testid="resources-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, author, tag..."
              className="w-full pl-11 pr-4 py-3 bg-ra-paper border border-ra-line outline-none focus:border-ra-ochre transition-colors text-ra-eucalypt placeholder:text-ra-light"
            />
          </div>

          <div className="md:col-span-3">
            <select
              data-testid="resources-type-filter"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 bg-ra-paper border border-ra-line outline-none focus:border-ra-ochre text-ra-eucalypt text-sm uppercase tracking-wider cursor-pointer"
            >
              <option value="all">All Types</option>
              {facets.types.map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <select
              data-testid="resources-topic-filter"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full px-4 py-3 bg-ra-paper border border-ra-line outline-none focus:border-ra-ochre text-ra-eucalypt text-sm uppercase tracking-wider cursor-pointer"
            >
              <option value="all">All Topics</option>
              {facets.topics.map((t) => (
                <option key={t} value={t}>{TOPIC_LABEL[t] || t}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="border-t border-ra-line px-6 lg:px-8 py-3 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-ra-soft">
          <span data-testid="resources-result-count">
            {loading ? "Searching…" : `${items.length} ${items.length === 1 ? "resource" : "resources"}`}
          </span>
          {(q || type !== "all" || topic !== "all") && (
            <button
              data-testid="resources-clear-filters"
              onClick={() => { setQ(""); setType("all"); setTopic("all"); }}
              className="text-ra-ochre hover:text-ra-eucalypt transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ra-line border border-ra-line">
        {items.map((r) => {
          const Icon = TYPE_ICON[r.type] || BookOpen;
          return (
            <article
              key={r.id}
              data-testid={`resource-card-${r.id}`}
              className="group bg-ra-bone p-7 lg:p-8 flex flex-col gap-5 min-h-[280px] hover:bg-ra-paper transition-colors duration-300 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 ra-overline">
                  <Icon size={14} className="text-ra-ochre" strokeWidth={1.6} />
                  {TYPE_LABEL[r.type] || r.type}
                </div>
                {r.year && <div className="text-xs text-ra-light tracking-widest">{r.year}</div>}
              </div>

              <h3 className="ra-serif text-2xl leading-tight flex-1 group-hover:text-ra-ochre transition-colors">
                {r.title}
              </h3>

              <p className="text-sm text-ra-soft leading-relaxed line-clamp-3">{r.summary}</p>

              <div className="flex items-center justify-between pt-4 border-t border-ra-line text-xs uppercase tracking-[0.18em]">
                <span className="text-ra-eucalypt">{r.author || "Restoration Australia"}</span>
                <span className="text-ra-ochre">{TOPIC_LABEL[r.topic] || r.topic}</span>
              </div>
            </article>
          );
        })}
        {!loading && items.length === 0 && (
          <div data-testid="resources-empty" className="col-span-full bg-ra-bone p-20 text-center">
            <div className="ra-serif text-3xl text-ra-soft">No resources match your search.</div>
            <p className="mt-3 text-ra-light">Try clearing filters or searching for a different term.</p>
          </div>
        )}
      </div>
    </div>
  );
}
